<template>
    <div class="flex flex-col">
        <p class="text-lg leading-7 font-medium text-black">Add a throttle between actions.</p>
        <p class="text-sm text-gray-700 pb-6">
            Control how many contacts are processed and when the workflow should end.
        </p>

        <div class="flex flex-col gap-y-6">
            <!-- Throttle Period Section -->
            <div class="flex flex-col gap-y-3">
                <p class="text-sm font-medium text-gray-700">Process contacts</p>
                <div class="flex items-center gap-x-2 justify-between">
                    <GgmsCommaInput v-model="throttleCount" placeholder="100" class="w-20"></GgmsCommaInput>
                    <span class="text-sm text-gray-700">every</span>
                    <GgmsCommaInput v-model="throttlePeriod" placeholder="1" class="w-20"></GgmsCommaInput>
                    <GgmsDropdown
                        v-model="throttleUnit"
                        :options="throttleUnitOptions"
                        optionLabel="label"
                        optionValue="value"
                        class="w-40"
                    ></GgmsDropdown>
                </div>
            </div>

            <!-- Termination Section -->
            <div class="flex flex-col gap-y-3">
                <p class="text-sm font-medium text-gray-700">End workflow after</p>
                <GgmsRadioSelect v-model="terminationType" :options="terminationOptions"></GgmsRadioSelect>

                <div v-if="terminationType === 'custom'" class="flex flex-col gap-y-2">
                    <div class="flex items-center gap-x-2 justify-start">
                        <GgmsCommaInput v-model="terminationValue" placeholder="60" class="w-32"></GgmsCommaInput>
                        <GgmsDropdown
                            v-model="terminationUnit"
                            :options="terminationUnitOptions"
                            optionLabel="label"
                            optionValue="value"
                            class="w-64 ml-1"
                        ></GgmsDropdown>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref, watch, onMounted } from "vue"
import GgmsDropdown from "@/components/GgmsDropdown.vue"
import GgmsRadioSelect from "@/components/GgmsRadioSelect.vue"
import { useWorkflowStore } from "@/stores/workflow"
import GgmsCommaInput from "@/components/GgmsCommaInput.vue"

defineEmits(["changeAction"])
const workflowStore = useWorkflowStore()

const throttleCount = ref(100)
const throttlePeriod = ref(1)
const throttleUnit = ref("minutes")

const terminationType = ref("allContactsProcessed")
const terminationValue = ref(60)
const terminationUnit = ref("minutes elapsed")

const throttleUnitOptions = [
    { label: "Minute/s", value: "minutes" },
    { label: "Hour/s", value: "hours" },
]

const terminationOptions = [
    { displayName: "All contacts processed", value: "allContactsProcessed" },
    { displayName: "Custom limit", value: "custom" },
]

const terminationUnitOptions = [
    { label: "Minutes elapsed", value: "minutes elapsed" },
    { label: "Hours elapsed", value: "hours elapsed" },
    { label: "Contacts processed", value: "contacts processed" },
]

function validateAndUpdateButtons() {
    const throttleCountValue = Number(throttleCount.value)
    const isValidThrottleCount = isNaN(throttleCountValue) || throttleCountValue > 0

    let isValidTermination = true

    if (terminationType.value === "custom") {
        const terminationValueValue = Number(terminationValue.value)
        isValidTermination = isNaN(terminationValueValue) || terminationValueValue > 0
    }

    const isValid = isValidThrottleCount && isValidTermination
    workflowStore.areButtonDisabled = !isValid
}

watch(
    [throttleCount, throttlePeriod, throttleUnit, terminationType, terminationValue, terminationUnit],
    () => {
        workflowStore.modelValue = {
            throttle: {
                count: throttleCount.value,
                period: throttlePeriod.value,
                unit: throttleUnit.value,
            },
            termination: {
                type: terminationType.value,
                ...(terminationType.value === "custom" && {
                    value: terminationValue.value,
                    unit: terminationUnit.value,
                }),
            },
        }
        validateAndUpdateButtons()
    },
    { deep: true }
)

onMounted(() => {
    if (workflowStore.modelValue?.throttle) {
        throttleCount.value = workflowStore.modelValue.throttle.count
        throttlePeriod.value = workflowStore.modelValue.throttle.period || 1
        throttleUnit.value = workflowStore.modelValue.throttle.unit
    }

    if (workflowStore.modelValue?.termination) {
        terminationType.value = workflowStore.modelValue.termination.type
        if (workflowStore.modelValue.termination.type === "custom") {
            terminationValue.value = workflowStore.modelValue.termination.value || 60
            terminationUnit.value = workflowStore.modelValue.termination.unit || "minutes elapsed"
        }
    }

    workflowStore.modelValue = {
        throttle: {
            count: throttleCount.value,
            period: throttlePeriod.value,
            unit: throttleUnit.value,
        },
        termination: {
            type: terminationType.value,
            ...(terminationType.value === "custom" && {
                value: terminationValue.value,
                unit: terminationUnit.value,
            }),
        },
    }

    validateAndUpdateButtons()
})
</script>
